import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

export default function LedgerPage() {
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState({
    entityType: 'ALL',
    entityId: 'ALL',
    accountId: 'ALL',
    fromDate: '',
    toDate: ''
  });

  const accountMap = useMemo(() => {
    const map = {};
    normalizeList(accounts).forEach((a) => { map[a.id] = `${a.code} - ${a.name}`; });
    return map;
  }, [accounts]);

  const entityMap = useMemo(() => {
    const map = {};
    normalizeList(customers).forEach((c) => { map[c.id] = c.shop_name || c.name; });
    normalizeList(vendors).forEach((v) => { map[v.id] = v.name; });
    return map;
  }, [customers, vendors]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [accountRes, customerRes, vendorRes] = await Promise.all([
        adminApi.getAccounts(),
        adminApi.getCustomers(),
        adminApi.getVendors()
      ]);
      setAccounts(normalizeList(accountRes.data));
      setCustomers(normalizeList(customerRes.data));
      setVendors(normalizeList(vendorRes.data));
    } catch (error) {
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        entity_type: filters.entityType === 'ALL' ? undefined : filters.entityType,
        entity_id: filters.entityId === 'ALL' ? undefined : filters.entityId,
        account_id: filters.accountId === 'ALL' ? undefined : filters.accountId,
        from_date: filters.fromDate || undefined,
        to_date: filters.toDate || undefined
      };
      const response = await adminApi.getLedger(params);
      setLines(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadLedger();
  }, [page, pageSize]);

  const handleApply = () => {
    setPage(1);
    loadLedger();
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const entityOptions = filters.entityType === 'VENDOR'
    ? normalizeList(vendors)
    : normalizeList(customers);

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Ledger</h1>
        <p className="text-[#A8A8A8] mt-1">Double entry journal lines</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Entity Type</Label>
              <Select
                value={filters.entityType}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, entityType: value, entityId: 'ALL' }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="CUSTOMER">Customer</SelectItem>
                  <SelectItem value="VENDOR">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Entity</Label>
              <Select
                value={filters.entityId}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, entityId: value }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {entityOptions.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.shop_name || entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Account</Label>
              <Select
                value={filters.accountId}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, accountId: value }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {normalizeList(accounts).map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">From</Label>
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">To</Label>
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleApply} className="bg-[#C97A5E] hover:bg-[#B06850]">Apply</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : lines.length === 0 ? (
            <div className="p-6 text-sm text-[#A8A8A8]">No ledger entries found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Entry No</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Account</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Entity</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Debit</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {lines.map((line) => (
                    <tr key={`${line.entry_id}-${line.account_id}-${line.debit}-${line.credit}`}>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{line.date ? new Date(line.date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{line.entry_no}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{accountMap[line.account_id] || line.account_id}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entityMap[line.entity_id] || '-'}</td>
                      <td className="py-3 px-4 text-sm text-right text-[#2D2420]">{formatCurrency(line.debit)}</td>
                      <td className="py-3 px-4 text-sm text-right text-[#2D2420]">{formatCurrency(line.credit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-sm text-[#A8A8A8]">Page {page} of {totalPages} · {total} lines</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            className="border-[#E5E5E5]"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            className="border-[#E5E5E5]"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
